import { useEffect, useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { usePortfolio } from '../../context/PortfolioContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const RANGE_OPTIONS = [
  { key: '1M', label: '1 month' },
  { key: '3M', label: '3 months' },
  { key: '6M', label: '6 months' },
  { key: 'YTD', label: 'Year to date' },
  { key: '1Y', label: '1 year' },
  { key: 'ALL', label: 'All time' },
];

const BASIS_OPTIONS = [
  { key: 'TOTAL_PNL', label: 'Total P&L' },
  { key: 'REALIZED_PLUS_DIVIDENDS', label: 'Realized + Dividends' },
];

const BENCHMARK_OPTIONS = [
  { key: 'NONE', label: 'No benchmark' },
  { key: 'SP500', label: 'S&P 500' },
  { key: 'NASDAQ', label: 'Nasdaq' },
  { key: 'IBEX', label: 'IBEX 35' },
  { key: 'FTSE', label: 'FTSE 100' },
];

function PnLChart() {
  const { pnlCurve, loadPnlCurve } = usePortfolio();
  const [range, setRange] = useState('3M');
  const [basis, setBasis] = useState('TOTAL_PNL');
  const [benchmark, setBenchmark] = useState('NONE');

  useEffect(() => {
    // Initial load when no data exists
    if (!pnlCurve) {
      loadPnlCurve(basis, benchmark);
    }
  }, [pnlCurve, loadPnlCurve, basis, benchmark]);

  // Re-fetch when user changes basis or benchmark
  useEffect(() => {
    loadPnlCurve(basis, benchmark);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basis, benchmark]);

  const filteredPoints = useMemo(() => {
    if (!pnlCurve?.points) return [];
    
    if (range === 'ALL') return pnlCurve.points;
    
    const points = pnlCurve.points;
    if (points.length === 0) return [];
    
    const lastDate = new Date(points[points.length - 1].date);
    let startDate;
    
    switch (range) {
      case '1M':
        startDate = new Date(lastDate.getFullYear(), lastDate.getMonth() - 1, lastDate.getDate());
        break;
      case '3M':
        startDate = new Date(lastDate.getFullYear(), lastDate.getMonth() - 3, lastDate.getDate());
        break;
      case '6M':
        startDate = new Date(lastDate.getFullYear(), lastDate.getMonth() - 6, lastDate.getDate());
        break;
      case '1Y':
        startDate = new Date(lastDate.getFullYear() - 1, lastDate.getMonth(), lastDate.getDate());
        break;
      case 'YTD':
        startDate = new Date(lastDate.getFullYear(), 0, 1);
        break;
      default:
        return points;
    }
    
    return points.filter(p => new Date(p.date) >= startDate);
  }, [pnlCurve, range]);

  const chartData = useMemo(() => {
    if (!filteredPoints.length) return null;

    const labels = filteredPoints.map(p => {
      const date = new Date(p.date);
      return `${date.getDate()}/${date.getMonth() + 1}`;
    });

    const datasets = [
      {
        label: 'P&L (GBP)',
        data: filteredPoints.map(p => p.pnl_gbp),
        borderColor: '#00d4ff',
        backgroundColor: (context) => {
          // Dynamic color based on value
          const chart = context.chart;
          const dataset = chart.data.datasets[context.datasetIndex];
          const values = dataset.data;
          
          // Create gradient that changes color at zero
          const {ctx, chartArea} = chart;
          if (!chartArea) return 'rgba(0, 212, 255, 0.1)';
          
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          const zeroY = chart.scales.y.getPixelForValue(0);
          const topRatio = Math.max(0, Math.min(1, (zeroY - chartArea.top) / (chartArea.bottom - chartArea.top)));
          
          // Green above zero, red below
          gradient.addColorStop(0, 'rgba(34, 197, 94, 0.3)');      // green-500 with opacity
          gradient.addColorStop(topRatio, 'rgba(34, 197, 94, 0.1)');
          gradient.addColorStop(Math.min(topRatio + 0.01, 1), 'rgba(239, 68, 68, 0.1)');  // red-500 with opacity
          gradient.addColorStop(1, 'rgba(239, 68, 68, 0.3)');
          
          return gradient;
        },
        fill: 'origin',
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 2,
        segment: {
          borderColor: ctx => {
            // Color based on average of segment points (p0 to p1)
            // This ensures whole segment is one solid color
            const p0 = ctx.p0.parsed.y;
            const p1 = ctx.p1.parsed.y;
            const avg = (p0 + p1) / 2;
            return avg >= 0 ? '#22c55e' : '#ef4444';  // green-500 or red-500
          }
        }
      },
    ];

    // Add zero line dataset
    datasets.push({
      label: 'Zero',
      data: filteredPoints.map(() => 0),
      borderColor: 'rgba(127, 140, 160, 0.5)',
      borderWidth: 1,
      pointRadius: 0,
      fill: false,
      tension: 0,
    });

    if (pnlCurve?.benchmark?.points?.length && benchmark !== 'NONE') {
      // Backend returns pnl_gbp directly (absolute values)
      // Create a map for O(1) lookup and handle missing dates gracefully
      const benchmarkMap = new Map();
      pnlCurve.benchmark.points.forEach(p => {
        benchmarkMap.set(p.date, p.pnl_gbp);
      });
      
      // Align benchmark data with portfolio dates
      // Use linear interpolation for missing dates to create smooth line
      const benchPoints = pnlCurve.benchmark.points;
      const benchData = filteredPoints.map(fp => {
        const val = benchmarkMap.get(fp.date);
        if (val !== undefined) return val;
        
        // Find nearest points for interpolation
        const fpDate = new Date(fp.date).getTime();
        let before = null, after = null;
        
        for (const bp of benchPoints) {
          const bpDate = new Date(bp.date).getTime();
          if (bpDate <= fpDate) before = bp;
          if (bpDate >= fpDate && !after) after = bp;
        }
        
        // Linear interpolation
        if (before && after && before !== after) {
          const t = (fpDate - new Date(before.date).getTime()) / 
                    (new Date(after.date).getTime() - new Date(before.date).getTime());
          return before.pnl_gbp + t * (after.pnl_gbp - before.pnl_gbp);
        }
        
        return before?.pnl_gbp ?? after?.pnl_gbp ?? null;
      });

      // Only add if we have valid data
      const validValues = benchData.filter(v => v !== null && !isNaN(v));
      if (validValues.length > 0) {
        datasets.push({
          label: pnlCurve.benchmark.name || 'Benchmark',
          data: benchData,
          borderColor: '#fb923c',  // Orange-400
          backgroundColor: 'transparent',
          borderWidth: 2,
          fill: false,
          tension: 0.4,  // Smoother curve
          pointRadius: 0,  // No points visible
          pointHoverRadius: 4,  // Small point on hover
          borderDash: [6, 4],  // Dashed line style
          spanGaps: true,  // Connect across null values
        });
      }
    }

    return { labels, datasets };
  }, [filteredPoints, pnlCurve, benchmark]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#7a99b8',
          font: { family: 'Space Mono', size: 11 },
          usePointStyle: true,
          pointStyle: 'circle',
          filter: (item, chart) => {
            // Hide the 'Zero' line from the legend
            return item.text !== 'Zero';
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(12, 28, 48, 0.96)',
        borderColor: 'rgba(0, 212, 255, 0.35)',
        borderWidth: 1,
        titleColor: '#dff0ff',
        bodyColor: '#dff0ff',
        padding: 10,
        filter: (tooltipItem) => {
          // Hide 'Zero' line from tooltip
          return tooltipItem.dataset.label !== 'Zero';
        },
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: 'GBP',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(29, 56, 85, 0.25)',
          drawBorder: false,
        },
        ticks: {
          color: '#7a99b8',
          font: { family: 'Space Mono', size: 10 },
          maxTicksLimit: 8,
        },
      },
      y: {
        grid: {
          color: 'rgba(29, 56, 85, 0.25)',
          drawBorder: false,
        },
        ticks: {
          color: '#7a99b8',
          font: { family: 'Space Mono', size: 10 },
          callback: (value) => {
            return new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: 'GBP',
              notation: 'compact',
              maximumFractionDigits: 1,
            }).format(value);
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  };

  if (!pnlCurve) {
    return (
      <div className="chart-container">
        <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span className="text-muted">Loading chart...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <div className="chart-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <span className="chart-title">Profit & Loss</span>
          
          <select 
            value={basis} 
            onChange={(e) => setBasis(e.target.value)}
            style={{ width: 'auto', fontSize: '0.75rem', padding: '0.375rem 0.5rem' }}
          >
            {BASIS_OPTIONS.map(opt => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
          
          <select 
            value={benchmark} 
            onChange={(e) => setBenchmark(e.target.value)}
            style={{ width: 'auto', fontSize: '0.75rem', padding: '0.375rem 0.5rem' }}
          >
            {BENCHMARK_OPTIONS.map(opt => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>

          {/* Compact range select — only visible on mobile (CSS-controlled) */}
          <select
            className="range-select"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            style={{ width: 'auto', fontSize: '0.75rem', padding: '0.375rem 0.5rem' }}
            aria-label="Time range"
          >
            {RANGE_OPTIONS.map(opt => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Pill buttons (desktop/tablet) */}
      <div
        className="range-pills"
        style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}
      >
        {RANGE_OPTIONS.map(opt => (
          <button
            key={opt.key}
            onClick={() => setRange(opt.key)}
            className="btn btn-sm"
            style={{
              background: range === opt.key ? 'var(--cyan)' : 'var(--bg-light)',
              color: range === opt.key ? 'var(--bg)' : 'var(--text)',
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="chart-wrapper">
        {chartData ? (
          <Line data={chartData} options={options} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <span className="text-muted">No data available</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default PnLChart;
